/** @format */

import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import Card from '../../../components/Card/Card.jsx';
import CardBody from '../../../components/Card/CardBody.jsx';
import CardHeader from '../../../components/Heading/CardHeaderExt';
import TuneIcon from '@material-ui/icons/Tune';
import GridContainer from '../../../components/Grid/GridContainer.jsx';
import GridItem from '../../../components/Grid/GridItem.jsx';
import CheckBoxSimple from '../../../components/Form/CheckBoxSimple.js';
import DatePickerForm from '../../../components/Form/DatePickerForm.js';
import { DateHelper } from '../../../helpers/DateHelper';
import TimePickerForm from '../../../components/Form/TimePickerForm.js';
import BillingFilterOptionsInterface from '../interfaces/BillingFilterOptionsInterface';
import I18n from '../../../components/Translate/I18n.js';
import { ItemLoaders } from '../../../core/loaders/ItemLoaders';
import { FormGeneralHelper } from '../../../helpers/FormGeneral.js';
import { configDefault } from '../../../core/config.js';
import { fractionsConstants } from '../../../modules/fractions/actions';
import { Fraction } from '../../../interfaces/platform/Fraction.js';
import { FractionCollectionInterface } from '../../../interfaces/collections/FractionCollectionInterface.js';
import CustomMultiSelect from '../../../components/Form/CustomMultiSelect';
import { platformResellerConstants } from '../../../modules/platformreseller/actions';
import { PlatformResellerCollectionInterface } from '../../../interfaces/collections/PlatformResellerCollectionInterface';
import SimpleSelectRest from '../../../components/Adescom/SimpleSelectRest';

interface BillingSearchContainerProps {
    classes: any;
    updateFilterState: (param: any, type: string) => void;
    billingFilter: BillingFilterOptionsInterface;
    changeBillingType: (event: any) => void;
    billingType: number;
    selectedPlatformReseller: number | null;
    setSelectedPlatformReseller: (id: number | null) => void;
}

export const BillingFilterContainer = (props: BillingSearchContainerProps): React.ReactElement => {
    const CLIENT_BILLING_TYPE = 0;
    const RESELLER_BILLING_TYPE = 1;
    const { setSelectedPlatformReseller } = props;

    const [fractions, setFractions] = useState<Array<Fraction>>([]);
    const [selectedFractions, setSelectedFractions] = useState<Array<string>>([]);
    const [selectedPlatformUsers, setSelectedPlatformUsers] = useState<Array<number>>([]);
    const [platformResellerList, setPlatformResellerList] = useState<PlatformResellerCollectionInterface | null>(null);

    const PLATFORM_RESELLER_URI = '/platform_resellers';

    const handleChangeMultiple = (event: any): void => {
        const { value } = event.target;
        props.updateFilterState(value, 'selectedFraction');
        setSelectedFractions(value);
    };

    const dispatch = useDispatch();
    useEffect(() => {
        const itemLoaders = new ItemLoaders(dispatch);
        itemLoaders.getListWithCallback(
            FormGeneralHelper.getByUri(FormGeneralHelper.getPath('/fractions', configDefault.PLATFORM_API_PREFIX)),
            fractionsConstants.LIST,
            [{ name: 'itemsPerPage', value: 999 }],
            [{ name: 'id', value: 'desc' }],
            (data: FractionCollectionInterface) => {
                if (data && data['hydra:member']) {
                    setFractions(data['hydra:member']);
                }
            },
            {
                propagateDataToCallback: true,
                dispatchAfterCallback: false,
            }
        );
    }, [dispatch]);

    const loadPlatformResellers = useCallback(
        (uri?: string | null): void => {
            if (!uri) {
                uri = FormGeneralHelper.getPath(PLATFORM_RESELLER_URI, configDefault.PLATFORM_API_PREFIX);
            }

            const loader = new ItemLoaders(dispatch);
            loader.getListWithCallback(
                FormGeneralHelper.getByUri(uri),
                platformResellerConstants.PLATFORM_RESELLER_LIST,
                [{ name: 'itemsPerPage', value: 9999 }],
                [],
                (data: PlatformResellerCollectionInterface) => {
                    setPlatformResellerList(data);
                    setSelectedPlatformReseller(data['hydra:member'][0].id);
                },
                {
                    propagateDataToCallback: true,
                    dispatchAfterCallback: false,
                }
            );
        },
        [dispatch, setSelectedPlatformReseller]
    );

    useEffect(() => {
        loadPlatformResellers();
    }, [loadPlatformResellers]);

    return (
        <Card>
            <CardHeader icon={<TuneIcon />} trans="general:common.additional_filters" />
            <CardBody>
                <GridContainer spacing={1}>
                    <GridItem xs={12} md={4} lg={2} style={{ marginBottom: 30 }}>
                        <SimpleSelectRest
                            callback={props.changeBillingType}
                            restElements={{
                                'hydra:member': [
                                    { name: 'tariffing:h.client_type', value: CLIENT_BILLING_TYPE },
                                    { name: 'tariffing:h.reseller_type', value: RESELLER_BILLING_TYPE },
                                ],
                            }}
                            stateName="billingType"
                            idState="billingType"
                            idSelected={props.billingType}
                            propKey="value"
                            required={false}
                            propValue="name"
                            emptyLabel="general:common.empty_label"
                            label="tariffing:h.type"
                        />
                        {props.billingType === CLIENT_BILLING_TYPE && platformResellerList && platformResellerList['hydra:member'] && (
                            <SimpleSelectRest
                                callback={(event: any): void => {
                                    props.setSelectedPlatformReseller(event.target.value);
                                }}
                                restElements={{
                                    'hydra:member': platformResellerList['hydra:member'].map((el) => {
                                        return { name: el.mainname, value: el.id };
                                    }),
                                }}
                                stateName="selectedPlatformReseller"
                                idState="selectedPlatformReseller"
                                idSelected={props.selectedPlatformReseller}
                                propKey="value"
                                required={false}
                                propValue="name"
                                emptyLabel="general:common.empty_label"
                                label="tariffing:h.reseller"
                            />
                        )}
                    </GridItem>
                </GridContainer>
                <GridContainer spacing={1}>
                    <GridItem xs={12} md={4} lg={2}>
                        <GridContainer>
                            <GridItem xs={12} md={12} lg={12}>
                                <CheckBoxSimple
                                    classes={props.classes}
                                    checked={props.billingFilter.incoming}
                                    label={'tariffing:h.incoming'}
                                    callback={(param: any): void => {
                                        props.updateFilterState(param, 'incoming');
                                    }}
                                />
                            </GridItem>
                            <GridItem xs={12} md={12} lg={12}>
                                <CheckBoxSimple
                                    classes={props.classes}
                                    checked={props.billingFilter.outgoing}
                                    label={'tariffing:h.outgoing'}
                                    callback={(param: any): void => {
                                        props.updateFilterState(param, 'outgoing');
                                    }}
                                />
                            </GridItem>
                            <GridItem xs={12} md={12} lg={12}>
                                <CheckBoxSimple
                                    classes={props.classes}
                                    checked={props.billingFilter.zeroDuration}
                                    label={'tariffing:h.include_zero_duration'}
                                    callback={(param: any): void => {
                                        props.updateFilterState(param, 'zeroDuration');
                                    }}
                                />
                            </GridItem>
                        </GridContainer>
                    </GridItem>
                    <GridItem xs={12} md={4} lg={2}>
                        <GridContainer spacing={1}>
                            <GridItem xs={12} md={6} lg={6}>
                                <I18n trans={'tariffing:h.date_from'} />
                                <DatePickerForm
                                    dateFormat={'YYYY-MM-DD'}
                                    timeFormat={false}
                                    defaultValue={DateHelper.formatYYYYMMDD(props.billingFilter.dateStartAt)}
                                    callback={(param: any): void => {
                                        props.updateFilterState(param, 'dateStartAt');
                                    }}
                                />
                            </GridItem>
                            <GridItem xs={12} md={6} lg={6}>
                                <I18n trans={'tariffing:h.time_from'} />
                                <TimePickerForm
                                    defaultValue={props.billingFilter.timeStartAt}
                                    callback={(param: any): void => {
                                        props.updateFilterState(param, 'timeStartAt');
                                    }}
                                />
                            </GridItem>
                            <GridItem xs={12} md={6} lg={6}>
                                <I18n trans={'tariffing:h.date_to'} />
                                <DatePickerForm
                                    timeFormat={false}
                                    dateFormat={'YYYY-MM-DD'}
                                    defaultValue={DateHelper.formatYYYYMMDD(props.billingFilter.dateEndAt)}
                                    callback={(param: any): void => {
                                        props.updateFilterState(param, 'dateEndAt');
                                    }}
                                />
                            </GridItem>
                            <GridItem xs={12} md={6} lg={6}>
                                <I18n trans={'tariffing:h.time_to'} />
                                <TimePickerForm
                                    timeFormat={true}
                                    defaultValue={props.billingFilter.timeEndAt}
                                    callback={(param: any): void => {
                                        props.updateFilterState(param, 'timeEndAt');
                                    }}
                                />
                            </GridItem>
                        </GridContainer>
                    </GridItem>
                    <GridItem xs={12} md={4} lg={3}>
                        <GridContainer spacing={1}>
                            <GridItem xs={12} md={6} lg={6}>
                                <CheckBoxSimple
                                    classes={props.classes}
                                    checked={props.billingFilter.call}
                                    label={'tariffing:h.conversation'}
                                    callback={(param: any): void => {
                                        props.updateFilterState(param, 'call');
                                    }}
                                />
                            </GridItem>
                            <GridItem xs={12} md={6} lg={6}>
                                <CheckBoxSimple
                                    classes={props.classes}
                                    checked={props.billingFilter.data}
                                    label={'tariffing:h.data'}
                                    callback={(param: any): void => {
                                        props.updateFilterState(param, 'data');
                                    }}
                                />
                            </GridItem>
                            <GridItem xs={12} md={6} lg={6}>
                                <CheckBoxSimple
                                    classes={props.classes}
                                    checked={props.billingFilter.sms}
                                    label={'tariffing:h.sms'}
                                    callback={(param: any): void => {
                                        props.updateFilterState(param, 'sms');
                                    }}
                                />
                            </GridItem>
                            <GridItem xs={12} md={6} lg={6}>
                                <CheckBoxSimple
                                    classes={props.classes}
                                    checked={props.billingFilter.mms}
                                    label={'tariffing:h.mms'}
                                    callback={(param: any): void => {
                                        props.updateFilterState(param, 'mms');
                                    }}
                                />
                            </GridItem>
                        </GridContainer>
                    </GridItem>
                    <GridItem xs={12} md={4} lg={3}>
                        <GridContainer>
                            <GridItem xs={12} md={12} lg={12}>
                                <CheckBoxSimple
                                    classes={props.classes}
                                    checked={props.billingFilter.onlyNonTarrified}
                                    label={'tariffing:h.only_non_tarrifing_connections'}
                                    callback={(param: any): void => {
                                        props.updateFilterState(param, 'onlyNonTarrified');
                                    }}
                                />
                            </GridItem>
                            <GridItem xs={12} md={12} lg={12}>
                                <CheckBoxSimple
                                    classes={props.classes}
                                    checked={props.billingFilter.onlySubscribe}
                                    label={'tariffing:h.only_record_belongs_to_client'}
                                    callback={(param: any): void => {
                                        props.updateFilterState(param, 'onlySubscribe');
                                    }}
                                />
                            </GridItem>
                            <GridItem xs={12} md={12} lg={12}>
                                <CheckBoxSimple
                                    classes={props.classes}
                                    checked={props.billingFilter.onlyService}
                                    label={'tariffing:h.only_record_belongs_to_services'}
                                    callback={(param: any): void => {
                                        props.updateFilterState(param, 'onlyService');
                                    }}
                                />
                            </GridItem>
                        </GridContainer>
                    </GridItem>
                    <GridItem xs={12} md={4} lg={2}>
                        <CustomMultiSelect
                            callback={handleChangeMultiple}
                            value={selectedFractions}
                            name={'fractionId'}
                            isElementsFromApi={false}
                            elements={fractions}
                            elementItemValue={'mainname'}
                            elementItemName={'mainname'}
                            label={'general:menu.fraction_list'}
                            showReversedSelect={false}
                        />
                    </GridItem>
                </GridContainer>
            </CardBody>
        </Card>
    );
};
